import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../api/client'
import type { PlanAdmin } from '../api/client'
import { useAuth } from '../auth/AuthContext'
import { useI18n } from '../i18n/I18nProvider'
import {
  Alert,
  Button,
  Card,
  CheckboxField,
  FormActions,
  Grid,
  PageHeader,
  SelectField,
  Stack,
  TextField,
  useToast,
} from '../components/ui'
import type { SelectOption } from '../components/ui'

/**
 * Les cinq formules et les trois modules sont des CODES, pas des libellés.
 *
 * Ils sont écrits ici parce qu'aucune route ne rend aujourd'hui le catalogue des formules à
 * un super-administrateur — la consultation d'un plan arrive au sous-projet 3. Ce n'est pas
 * un doublon de la base : la base reste seule juge, et refuse un code qu'elle ne connaît pas
 * par `platform.plan_unknown` ou `platform.module_unknown`. Une liste qui aurait divergé du
 * catalogue se ferait donc corriger au premier envoi, avec un message lisible, plutôt que de
 * créer un client sous une formule imaginaire.
 */
import { MODULES, nomFormule } from './formules'

/**
 * Créer un client, en un acte.
 *
 * TROIS GROUPES, DANS L'ORDRE OÙ ON LES REMPLIT : l'entreprise, son premier
 * administrateur, ce qu'elle achète. L'ordre n'est pas cosmétique — il suit ce que la
 * création pose réellement, et les trois partent ensemble : `core.platform_create_client`
 * écrit l'entreprise, l'administrateur, l'abonnement et les modules dans une transaction,
 * ou n'écrit rien. Un client à moitié créé est l'impasse que ce lot supprime.
 *
 * AUCUN CHAMP NE GRISE LE BOUTON. Chaque refus porte un code stable — raison sociale vide,
 * aucun module, courriel déjà pris — et chacun a sa traduction dans les trois langues. Un
 * bouton grisé sans explication apprend moins qu'un message qui nomme ce qui manque.
 *
 * LE MOT DE PASSE INITIAL EST DIT POUR CE QU'IL EST, à l'écran et non dans ce commentaire :
 * il se transmet hors du logiciel, parce qu'il n'existe aujourd'hui ni service de messagerie
 * ni écran de changement de mot de passe (spécification §0.3). Le super-administrateur doit
 * le savoir au moment où il le saisit, pas le découvrir au premier appel du client.
 */
export function NewClientPage() {
  const { t, language, describeError } = useI18n()
  const { session } = useAuth()
  const { notify } = useToast()
  const navigate = useNavigate()

  const token = session?.token ?? ''

  const [legalName, setLegalName] = useState('')
  const [governorate, setGovernorate] = useState('')
  const [email, setEmail] = useState('')
  const [adminName, setAdminName] = useState('')
  const [adminEmail, setAdminEmail] = useState('')
  const [adminPassword, setAdminPassword] = useState('')
  // LES FORMULES VIENNENT DE LA BASE. Depuis 0076 le panneau en crée : une liste écrite
  // ici ne proposerait jamais une formule nouvelle, et une formule archivée resterait
  // offerte jusqu'au platform.plan_unknown du clic. Seules les formules vendables sont
  // listées, publiques d'abord.
  const [formules, setFormules] = useState<PlanAdmin[]>([])
  const [planCode, setPlanCode] = useState<string>('')
  const [modules, setModules] = useState<Set<string>>(new Set([MODULES[0]]))
  const [envoi, setEnvoi] = useState(false)

  const basculerModule = (code: string, coche: boolean) =>
    setModules((actuels) => {
      const suivant = new Set(actuels)
      if (coche) suivant.add(code)
      else suivant.delete(code)
      return suivant
    })

  useEffect(() => {
    if (token === '') return

    const controle = new AbortController()

    api
      .listPlatformPlans(token, controle.signal)
      .then((plans) => {
        const vendables = plans
          .filter((p) => p.isActive)
          .sort((a, b) => Number(b.isPublic) - Number(a.isPublic) || a.displayOrder - b.displayOrder)
        setFormules(vendables)
        setPlanCode((actuel) => (actuel === '' && vendables.length > 0 ? (vendables[0]?.code ?? actuel) : actuel))
      })
      .catch((cause: unknown) => {
        if (!controle.signal.aborted) notify(describeError(cause), 'erreur')
      })

    return () => controle.abort()
  }, [token, notify, describeError])

  const optionsPlans: SelectOption[] = formules.map((p) => ({
    value: p.code,
    label: nomFormule(language, p),
  }))

  const creer = async () => {
    setEnvoi(true)

    try {
      await api.createPlatformClient(token, {
        legalName: legalName.trim(),
        governorate: governorate.trim(),
        email: email.trim(),
        adminName: adminName.trim(),
        adminEmail: adminEmail.trim(),
        adminPassword,
        planCode,
        modules: [...modules],
      })

      notify(t('platform.clients.created'), 'succes')
      navigate('/platform')
    } catch (cause: unknown) {
      notify(describeError(cause), 'erreur')
    } finally {
      setEnvoi(false)
    }
  }

  return (
    <Stack gap={4}>
      <PageHeader title={t('platform.clients.new')} description={t('platform.clients.new.description')} />

      <Card title={t('platform.clients.group.company')}>
        <Grid min="15rem">
          <TextField label={t('platform.clients.legalName')} value={legalName} onChange={setLegalName} required />
          <TextField label={t('platform.clients.governorate')} value={governorate} onChange={setGovernorate} />
          <TextField label={t('platform.clients.email')} value={email} onChange={setEmail} type="email" ltr />
        </Grid>
      </Card>

      <Card title={t('platform.clients.group.admin')}>
        <Stack gap={3}>
          {/* Le fait à dire au moment de la saisie, pas après : personne ne pourra changer
              ce mot de passe depuis le produit, et aucun courriel ne part. */}
          <Alert tone="avertissement">{t('platform.clients.passwordWarning')}</Alert>

          <Grid min="15rem">
            <TextField label={t('platform.clients.adminName')} value={adminName} onChange={setAdminName} required />
            <TextField
              label={t('platform.clients.adminEmail')}
              value={adminEmail}
              onChange={setAdminEmail}
              type="email"
              ltr
              required
            />
            <TextField
              label={t('platform.clients.adminPassword')}
              value={adminPassword}
              onChange={setAdminPassword}
              type="password"
              hint={t('platform.clients.passwordHint')}
              ltr
              required
            />
          </Grid>
        </Stack>
      </Card>

      <Card title={t('platform.clients.group.plan')}>
        <Stack gap={3}>
          <Grid min="15rem">
            <SelectField
              label={t('platform.clients.plan')}
              value={planCode}
              options={optionsPlans}
              onChange={setPlanCode}
              required
            />

            <Stack gap={2}>
              <span className="text-[0.85rem] font-medium">{t('platform.clients.modules')}</span>
              {MODULES.map((code) => (
                <CheckboxField
                  key={code}
                  label={t(`module.${code}` as never)}
                  checked={modules.has(code)}
                  onChange={(coche) => basculerModule(code, coche)}
                />
              ))}
              <p className="m-0 text-[0.8rem] text-texte-doux">{t('platform.clients.modulesHint')}</p>
            </Stack>
          </Grid>

          <FormActions>
            <Button onClick={() => void creer()} pending={envoi} pendingLabel={t('common.loading')}>
              {t('common.create')}
            </Button>
            <Button variant="secondaire" onClick={() => navigate('/platform')} disabled={envoi}>
              {t('common.cancel')}
            </Button>
          </FormActions>
        </Stack>
      </Card>
    </Stack>
  )
}
